import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Empty, Image, Input, InputNumber, Select, Space, Table, Typography, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  addCatalogItem,
  assignCatalogToAccount,
  listContractorAccounts,
  searchCatalogProducts,
  saveCatalog,
} from '../../services/contractorsHubApi';
import { useProductImageBaseUrl } from './useProductImageBaseUrl';
import { buildProductImageUrl } from '../../pages/orderXV4Utils';

const { Text } = Typography;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const asString = (value) => {
  if (value == null) return '';
  return Array.isArray(value) ? asString(value[0]) : String(value);
};

const toSelectableRow = (doc) => ({
  key: asString(doc.productCode || doc.id),
  sku: asString(doc.productCode),
  name: asString(doc.name),
  description: asString(doc.description),
  manufacturerName: asString(doc.manufacturerName),
  imageUrl: asString(doc.imageUrl),
});

const SearchProductMasterModal = ({ open, onClose, onSaved, masterId }) => {
  const addingToExisting = Boolean(masterId);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  // The query that actually produced `results`/`total` -- distinct from
  // `query` (the live input value), so the "N results for ..." summary
  // doesn't change while the user is still typing, only once a search for
  // that text has actually completed.
  const [searchedQuery, setSearchedQuery] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [catalogKey, setCatalogKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [contractorAccounts, setContractorAccounts] = useState([]);
  const [contractorAccountId, setContractorAccountId] = useState(undefined);
  const imageBaseUrl = useProductImageBaseUrl();

  const reset = () => {
    setQuery('');
    setResults([]);
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setTotal(0);
    setSearchedQuery(null);
    setSelectedKeys([]);
    setSelectedRows({});
    setCatalogKey('');
    setDisplayName('');
    setSearchError('');
    setContractorAccountId(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (open && !addingToExisting) {
      listContractorAccounts()
        .then(setContractorAccounts)
        .catch(() => setContractorAccounts([]));
    }
  }, [open, addingToExisting]);

  const runSearch = async (nextPage, nextPageSize) => {
    setSearching(true);
    setSearchError('');
    const trimmedQuery = query.trim();
    try {
      const data = await searchCatalogProducts({ query: trimmedQuery, page: nextPage, pageSize: nextPageSize });
      const docs = Array.isArray(data?.docs) ? data.docs : [];
      setResults(docs.map(toSelectableRow));
      setTotal(Number(data?.numFound || 0));
      setSearchedQuery(trimmedQuery);
      setPage(nextPage);
      setPageSize(nextPageSize);
    } catch (err) {
      setSearchError(err.message || 'Could not search the product master');
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = () => runSearch(1, pageSize);

  const handleTableChange = (pagination) => {
    runSearch(pagination.current, pagination.pageSize);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) runSearch(1, DEFAULT_PAGE_SIZE);
  }, [open]);

  const handleSelectionChange = (keys, rows) => {
    setSelectedKeys(keys);
    setSelectedRows((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.key] = {
          ...row,
          listPrice: next[row.key]?.listPrice ?? 0,
          customerPrice: next[row.key]?.customerPrice ?? 0,
        };
      });
      Object.keys(next).forEach((key) => {
        if (!keys.includes(key)) delete next[key];
      });
      return next;
    });
  };

  const handleRemoveSelected = (key) => {
    setSelectedKeys((prev) => prev.filter((k) => k !== key));
    setSelectedRows((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleListPriceChange = (key, listPrice) => {
    setSelectedRows((prev) => ({ ...prev, [key]: { ...prev[key], listPrice } }));
  };

  const handleCustomerPriceChange = (key, customerPrice) => {
    setSelectedRows((prev) => ({ ...prev, [key]: { ...prev[key], customerPrice } }));
  };

  const selectedItems = selectedKeys.map((key) => selectedRows[key]).filter(Boolean);

  const handleAddToExisting = async () => {
    if (!selectedItems.length) {
      message.error('Select at least one product to add');
      return;
    }
    setSaving(true);
    try {
      for (const item of selectedItems) {
        await addCatalogItem(masterId, {
          sku: asString(item.sku),
          name: asString(item.name),
          listPrice: Number(item.listPrice) || 0,
          customerPrice: Number(item.customerPrice) || 0,
          description: asString(item.description) || asString(item.name),
          imageUrl: asString(item.imageUrl),
        });
      }
      message.success(`Added ${selectedItems.length} item(s) to the catalog`);
      reset();
      onSaved();
    } catch (err) {
      message.error(err.message || 'Could not add items to the catalog');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!catalogKey.trim() || !displayName.trim()) {
      message.error('Catalog Key and Display Name are required');
      return;
    }
    if (!selectedItems.length) {
      message.error('Select at least one product from the search results');
      return;
    }
    setSaving(true);
    try {
      const result = await saveCatalog({
        catalogKey,
        displayName,
        items: selectedItems.map(({ sku, name, listPrice, customerPrice, description, imageUrl }) => ({
          sku: asString(sku),
          name: asString(name),
          list_price: Number(listPrice) || 0,
          customer_price: Number(customerPrice) || 0,
          description: asString(description) || asString(name),
          image_url: asString(imageUrl),
        })),
      });
      if (contractorAccountId) {
        await assignCatalogToAccount(contractorAccountId, result.master_id);
      }
      message.success(`Catalog created with ${selectedItems.length} item(s)`);
      reset();
      onSaved();
    } catch (err) {
      message.error(err.message || 'Could not save catalog');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = addingToExisting ? handleAddToExisting : handleCreate;
  const submitLabel = addingToExisting
    ? `Add to Catalog (${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'})`
    : `Create Catalog (${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'})`;

  return (
    <Drawer
      title="Add Products from Product Master"
      open={open}
      onClose={handleClose}
      placement="right"
      width="100%"
      destroyOnClose
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={saving} disabled={!selectedItems.length}>
            {submitLabel}
          </Button>
        </Space>
      }
    >
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
        {!addingToExisting && (
          <Space wrap style={{ width: '100%', marginBottom: 12 }}>
            <Input
              placeholder="Catalog Key (e.g. hvac-standard)"
              value={catalogKey}
              onChange={(e) => setCatalogKey(e.target.value)}
              style={{ width: 240 }}
            />
            <Input
              placeholder="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={{ width: 240 }}
            />
            <Select
              placeholder="Associate with contractor (optional)"
              allowClear
              style={{ width: 260 }}
              value={contractorAccountId}
              onChange={setContractorAccountId}
              options={contractorAccounts.map((a) => ({ value: a.id, label: a.display_name }))}
            />
          </Space>
        )}
        <Text type="secondary">
          Search the product master and select the products to add. List price and customer price default to 0 and
          can be edited before saving.
        </Text>
        <Space style={{ marginTop: 12, width: '100%' }}>
          <Input.Search
            placeholder="Search by name, SKU, or description"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onSearch={handleSearch}
            loading={searching}
            style={{ width: 420 }}
            allowClear
          />
        </Space>
        {searchError && <Alert type="error" message={searchError} style={{ marginTop: 12 }} />}
        {!searchError && searchedQuery !== null && !searching && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {searchedQuery
              ? `${total} result${total === 1 ? '' : 's'} for "${searchedQuery}"`
              : `${total} product${total === 1 ? '' : 's'} total`}
          </Text>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          <Table
            size="small"
            rowKey="key"
            loading={searching}
            dataSource={results}
            onChange={handleTableChange}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              showTotal: (t) => `${t} product${t === 1 ? '' : 's'}`,
            }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: handleSelectionChange,
              preserveSelectedRowKeys: true,
            }}
            columns={[
              {
                title: 'Image',
                dataIndex: 'imageUrl',
                width: 72,
                render: (url) => {
                  const resolved = buildProductImageUrl(url, imageBaseUrl);
                  return resolved ? (
                    <Image src={resolved} alt="" width={48} height={48} style={{ objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: 48, height: 48, background: '#f0f0f0', borderRadius: 4 }} />
                  );
                },
              },
              { title: 'SKU', dataIndex: 'sku' },
              { title: 'Name', dataIndex: 'name' },
              { title: 'Manufacturer', dataIndex: 'manufacturerName' },
            ]}
          />
        </div>

        <div
          style={{
            width: 380,
            flexShrink: 0,
            borderLeft: '1px solid #f0f0f0',
            overflow: 'auto',
            padding: '16px 24px',
            background: 'rgba(0,0,0,0.02)',
          }}
        >
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Selected Items ({selectedItems.length})
          </Text>
          {selectedItems.length === 0 ? (
            <Empty description="No items selected yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {selectedItems.map((item) => (
                <div
                  key={item.key}
                  style={{
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    padding: 12,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <Text strong>{item.sku}</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.name}
                        </Text>
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveSelected(item.key)}
                    />
                  </div>
                  <Space style={{ marginTop: 8, width: '100%' }}>
                    <InputNumber
                      addonBefore="List"
                      min={0}
                      step={0.01}
                      value={item.listPrice}
                      onChange={(value) => handleListPriceChange(item.key, value)}
                      style={{ width: 165 }}
                    />
                    <InputNumber
                      addonBefore="Cust"
                      min={0}
                      step={0.01}
                      value={item.customerPrice}
                      onChange={(value) => handleCustomerPriceChange(item.key, value)}
                      style={{ width: 165 }}
                    />
                  </Space>
                </div>
              ))}
            </Space>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default SearchProductMasterModal;
